import { useEffect, useLayoutEffect, useState } from "react";

let lockCount = 0;
let savedScrollY = 0;
let portalChromeHidden = false;

type ChromeListener = (hidden: boolean) => void;
const chromeListeners = new Set<ChromeListener>();

const notifyChromeListeners = (): void => {
  for (const listener of chromeListeners) {
    listener(portalChromeHidden);
  }
};

const setPortalChromeHidden = (hidden: boolean): void => {
  if (portalChromeHidden === hidden) {
    return;
  }
  portalChromeHidden = hidden;
  notifyChromeListeners();
};

const applyLock = (): void => {
  if (lockCount === 0) {
    savedScrollY = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${savedScrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";
    setPortalChromeHidden(true);
  }
  lockCount += 1;
};

const releaseLock = (): void => {
  lockCount -= 1;
  if (lockCount > 0) {
    return;
  }

  document.body.style.position = "";
  document.body.style.top = "";
  document.body.style.left = "";
  document.body.style.right = "";
  document.body.style.width = "";
  document.body.style.overflow = "";
  window.scrollTo(0, savedScrollY);
  setPortalChromeHidden(false);
};

/**
 * Prevents the document from scrolling while a mobile pinned edit card is open.
 * Uses a ref count so nested/overlapping locks (e.g. unmount during transition)
 * do not restore scroll prematurely. The iOS-safe `position: fixed` + saved
 * `scrollY` pattern — inner card scroll still works via its own overflow.
 *
 * While locked, also signals `useIsPortalChromeHidden` so `PortalPageLayout`
 * can drop the mobile header and bottom nav (full-viewport edit takeover).
 */
export const useBodyScrollLock = (locked: boolean): void => {
  // Layout effect so portal chrome hides before paint (no header/nav flash).
  useLayoutEffect(() => {
    if (!locked) {
      return;
    }

    applyLock();

    return (): void => {
      releaseLock();
    };
  }, [locked]);
};

/**
 * True while any `useBodyScrollLock(true)` caller is active — used by the
 * portal shell to hide the mobile header and bottom nav during pinned edits.
 */
export const useIsPortalChromeHidden = (): boolean => {
  const [hidden, setHidden] = useState(portalChromeHidden);

  useEffect(() => {
    chromeListeners.add(setHidden);
    setHidden(portalChromeHidden);

    return (): void => {
      chromeListeners.delete(setHidden);
    };
  }, []);

  return hidden;
};

/** Test helper — resets module state between specs. */
export const resetBodyScrollLockForTests = (): void => {
  if (lockCount > 0) {
    lockCount = 1;
    releaseLock();
  }
  lockCount = 0;
  savedScrollY = 0;
  setPortalChromeHidden(false);
};
